
import Vue from "vue"
import UserSettings from "@/views/account/UserSettings.vue"
import AccountConnections from "@/views/account/AccountConnections.vue"
import SpaceStatistics from "@/components/SpaceStatistic.vue"
import {
  accountPathForTab,
  accountTabIndexFromPath,
  queryParamAsString,
  shouldOpenAccountConnectionsFromQuery,
  shouldOpenAccountProgressionFromQuery,
} from "@/utils/listParagonRouteQuery"
import type { AccountTabIndex, RouteQuery } from "@/utils/listParagonRouteQuery"
import Progression from "@/views/Progression.vue"
import { hasPermission } from "@/plugins/token"

const ACCOUNT_OAUTH_QUERY_KEYS = ['google', 'microsoft'] as const

function accountConnectionsQueryFromRoute(query: RouteQuery): Record<string, string> {
  const result: Record<string, string> = {}
  for (const key of ACCOUNT_OAUTH_QUERY_KEYS) {
    const value = queryParamAsString(query, key)
    if (value) result[key] = value
  }
  return result
}

function stripLegacyAccountQuery(query: RouteQuery): Record<string, string> {
  const result: Record<string, string> = {}
  for (const key of Object.keys(query)) {
    if (key === 'tab') continue
    const value = queryParamAsString(query, key)
    if (value) result[key] = value
  }
  return result
}

export default Vue.extend({
  components: {
    UserSettings,
    AccountConnections,
    SpaceStatistics,
    Progression,
  },
  data() {
    return {
      tab: 0 as AccountTabIndex,
    }
  },
  computed: {
    canAccessProgression(): boolean {
      return hasPermission("progression")
    },
  },
  watch: {
    "$route.path": {
      handler() {
        this.syncTabFromRoute()
        this.resolveLegacyAccountRoute()
      },
      immediate: true,
    },
    "$route.query": {
      handler() {
        this.resolveLegacyAccountRoute()
      },
      deep: true,
    },
  },
  created() {
    document.title = this.$vuetify.lang.t("$vuetify.user_settings")
  },
  methods: {
    syncTabFromRoute(): void {
      let tabFromPath = accountTabIndexFromPath(this.$route.path)
      if (tabFromPath === 3 && !this.canAccessProgression) {
        tabFromPath = 0
      }
      if (this.tab !== tabFromPath) {
        this.tab = tabFromPath
      }
    },
    onAccountTabChange(tab: AccountTabIndex): void {
      if (tab === 3 && !this.canAccessProgression) {
        return
      }
      if (this.tab === tab) return
      const targetPath = accountPathForTab(tab)
      if (targetPath === this.$route.path) {
        this.tab = tab
        return
      }
      const query = tab === 1
        ? accountConnectionsQueryFromRoute(this.$route.query as RouteQuery)
        : stripLegacyAccountQuery(this.$route.query as RouteQuery)
      this.$router.push({ path: targetPath, query })
    },
    resolveLegacyAccountRoute(): void {
      if (!this.$router || !this.$route) return
      const query = this.$route.query as RouteQuery

      if (shouldOpenAccountProgressionFromQuery(query)) {
        if (!this.canAccessProgression) {
          return
        }
        if (this.$route.path === '/account/progression') return
        const replaceResult = this.$router.replace({
          path: '/account/progression',
          query: stripLegacyAccountQuery(query),
        })
        if (replaceResult && typeof replaceResult.catch === 'function') {
          replaceResult.catch(() => undefined)
        }
        return
      }

      if (!shouldOpenAccountConnectionsFromQuery(query)) return
      if (this.$route.path === '/account/connections') return

      const targetQuery = {
        ...accountConnectionsQueryFromRoute(query),
        ...stripLegacyAccountQuery(query),
      }
      const replaceResult = this.$router.replace({
        path: '/account/connections',
        query: targetQuery,
      })
      if (replaceResult && typeof replaceResult.catch === 'function') {
        replaceResult.catch(() => undefined)
      }
    },
  },
})
